import { useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  DialogContentText,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Key as KeyIcon } from '@mui/icons-material';
import { useDeleteQuickLogins, useDeviceQuickLogins } from '@/hooks/use-quicklogins';
import { usePermissions } from '@/hooks/use-permissions';
import { useTimezone } from '@/hooks/use-timezone';
import { formatRelativeTime, isRecent } from '@/lib/timezone';
import { notificationStore } from '@/stores/notification.store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { colors, radius } from '@/theme/tokens';

interface DeviceQuickLoginCardProps {
  deviceId: string;
  /** How the device is named in the delete dialog, e.g. its machine id. */
  deviceLabel: string;
}

/**
 * Overview card for the machine's Quick Login backup on game-server: whether
 * one exists and when the machine last updated it. Account data never reaches
 * the browser. Support can delete the backup (device:write); the machine keeps
 * its local list and backs it up again on its next change.
 */
export function DeviceQuickLoginCard({ deviceId, deviceLabel }: DeviceQuickLoginCardProps) {
  const { can } = usePermissions();
  const { data, isLoading, error, refetch } = useDeviceQuickLogins(deviceId);
  const deleteMutation = useDeleteQuickLogins(deviceId);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const backedUp = !!data?.exist;

  const handleDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setDeleteOpen(false);
        notificationStore.getState().show('Quick Login backup deleted', 'success');
      },
    });
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon sx={{ color: '#666666', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Quick Login Backup
            </Typography>
          </Box>
        }
        subheader="Used to restore Quick Login accounts after a factory reset or a corrupted list."
        action={
          can.editDevices && (
            <Button
              variant="outlined"
              size="small"
              disabled={!backedUp}
              onClick={() => {
                deleteMutation.reset();
                setDeleteOpen(true);
              }}
              sx={{
                borderColor: 'divider',
                color: colors.primary.main,
                borderRadius: `${radius.md}px`,
                textTransform: 'none',
              }}
            >
              Delete
            </Button>
          )
        }
        sx={{ pb: 0, '& .MuiCardHeader-action': { alignSelf: 'flex-start', m: 0, ml: 2 } }}
      />
      <CardContent sx={{ pt: 1.5 }}>
        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Couldn't load Quick Login backup.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Row label="Status">
              {isLoading ? (
                <Skeleton width={90} />
              ) : (
                <Typography variant="body2" fontSize={12}>
                  {backedUp ? 'Backed up' : 'Not backed up'}
                </Typography>
              )}
            </Row>
            <Row label="Last backup">
              {isLoading ? (
                <Skeleton width={70} />
              ) : backedUp && data?.updatedAt ? (
                <LastBackup value={data.updatedAt} />
              ) : (
                <Typography variant="body2" fontSize={12} color="text.secondary">
                  -
                </Typography>
              )}
            </Row>
          </Box>
        )}
      </CardContent>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        destructive
        title="Delete Quick Login backup?"
        description={
          <>
            <DialogContentText sx={{ mb: 1.5 }}>
              <strong>{deviceLabel}</strong> won't be able to restore its Quick Login accounts after a
              factory reset or if its local list is corrupted.
            </DialogContentText>
            <DialogContentText>
              The machine keeps its current accounts. Adding, removing or changing an account on it
              creates a new backup.
            </DialogContentText>
          </>
        }
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        loadingLabel="Deleting…"
        error={deleteMutation.error ? errorMessage(deleteMutation.error, 'Delete failed.') : undefined}
      />
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** LastBackup shows the time like FormattedTime, with the full time and what
 * updates it on hover. */
function LastBackup({ value }: { value: string }) {
  const { formatTime } = useTimezone();
  const text = (isRecent(value) && formatRelativeTime(value)) || formatTime(value);

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <>
          {formatTime(value)}
          <br />
          Updated when an account is added, removed or changed on the machine.
        </>
      }
    >
      <Typography
        variant="body2"
        fontSize={12}
        component="span"
        sx={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { detail?: string; message?: string } }; message?: string };
  return e?.response?.data?.detail || e?.response?.data?.message || e?.message || fallback;
}
